// La card de `crear_pdf`: vista previa + descarga — ADR-2026-09-08-el-agente-lee-y-crea-pdfs.
//
// Se auto-resuelve al montar, como las otras cards de mostrar: armar un documento no cuesta
// créditos y no hay nada que confirmar. Los bytes se generan recién al tocar Descargar.
//
// La vista previa NO es el PDF: es el contenido dibujado en HTML. Renderizar la página real
// requeriría el canvas de pdf.js para algo que el usuario va a abrir en su lector igual, y
// lo que necesita ver acá es si el CONTENIDO es el correcto.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Download, FileType2, Loader2 } from 'lucide-react';
import { mapWithConcurrency } from '../../../utils/concurrency';
import { escribirPdf, textoDeItem, type ImagenPdf } from '../../../utils/pdf/escribirPdf';
import { imagenParaPdf } from '../../../utils/pdf/imagenParaPdf';
import { contarBloques, normalizarPdfCreado } from './pdf';
import type { UseV2ChatReturn, V2ToolCard } from '../useV2Chat';

/** Bloques de la vista previa. El resto está en el archivo. */
const BLOQUES_PREVIA = 8;

export const CrearPdfCard: React.FC<{
  card: V2ToolCard;
  chat: UseV2ChatReturn;
}> = ({ card, chat }) => {
  const doc = useMemo(() => normalizarPdfCreado(card.input), [card.input]);
  const [error, setError] = useState<string | null>(null);
  const [armando, setArmando] = useState(false);
  const resueltoRef = useRef(false);

  useEffect(() => {
    if (!resueltoRef.current && card.status === 'pending') {
      resueltoRef.current = true;
      const { bloques, palabras } = contarBloques(doc);
      chat.resolveToolCard(card.id, {
        created: true,
        nombre: doc.nombre,
        bloques,
        palabras,
        // El documento entero viaja en la respuesta para el `[registro:]` del historial.
        pdf: doc,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const descargar = async () => {
    if (armando) return;
    setError(null);
    setArmando(true);
    try {
      // Las imágenes se bajan RECIÉN acá: un documento que nadie descarga no cuesta ni una
      // descarga (ADR-2026-09-08-el-pdf-con-estilos-e-imagenes). Las que fallan se saltean
      // y el resto del documento sale igual.
      const refs = Object.keys(doc.imagenes);
      const bajadas = await mapWithConcurrency(refs, 4, (ref) => imagenParaPdf(doc.imagenes[ref]));
      const imagenes: Record<string, ImagenPdf> = {};
      refs.forEach((ref, i) => {
        const img = bajadas[i];
        if (img) imagenes[ref] = img;
      });
      const fallidas = refs.length - Object.keys(imagenes).length;
      const bytes = escribirPdf({
        titulo: doc.titulo,
        bloques: doc.bloques,
        imagenes,
        ...(doc.color ? { color: doc.color } : {}),
        ...(doc.pie ? { pie: doc.pie } : {}),
      });
      const url = URL.createObjectURL(
        new Blob([bytes as unknown as BlobPart], { type: 'application/pdf' }),
      );
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = doc.nombre;
      document.body.appendChild(a);
      a.click();
      // Diferido: revocar en el mismo tick cancela la descarga en algunos navegadores.
      setTimeout(() => {
        URL.revokeObjectURL(url);
        a.remove();
      }, 200);
      if (fallidas > 0) {
        setError(`Bajé el PDF, pero ${fallidas} imagen(es) no se pudieron cargar.`);
      }
    } catch (err) {
      console.warn('[CrearPdf] descarga', err);
      setError('No pude armar el PDF. Probá de nuevo.');
    }
    setArmando(false);
  };

  const previa = doc.bloques.slice(0, BLOQUES_PREVIA);
  const ocultos = doc.bloques.length - previa.length;
  const { palabras, imagenes: cuantasImagenes } = contarBloques(doc);

  return (
    <div className="rounded-2xl border border-[#02062B]/[0.08] bg-white p-3">
      <div className="flex items-start justify-between gap-2 px-1 pb-2">
        <span className="flex min-w-0 items-center gap-2">
          <FileType2 className="h-4 w-4 shrink-0 text-[#02062B]/[0.45]" aria-hidden="true" />
          <span className="min-w-0">
            <span className="block truncate text-[14px] font-medium text-brand-black">
              {doc.nombre}
            </span>
            <span className="block text-[12px] text-[#02062B]/[0.55]">
              {doc.bloques.length} bloque(s) · ~{palabras} palabras
              {cuantasImagenes > 0 ? ` · ${cuantasImagenes} imagen(es)` : ''}
            </span>
          </span>
        </span>
        <button
          type="button"
          onClick={() => void descargar()}
          disabled={armando}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-[#F0F2F7] py-1.5 px-3 text-[12.5px] font-medium text-[#02062B]/[0.75] transition-colors hover:bg-[#02062B]/[0.09] disabled:opacity-50"
        >
          {armando ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          ) : (
            <Download className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          {armando ? 'Armando…' : 'Descargar PDF'}
        </button>
      </div>

      {/* La hoja: fondo blanco y sangría, para que se lea como un documento. */}
      <div className="max-h-[320px] overflow-y-auto rounded-xl border border-[#02062B]/[0.06] bg-white px-4 py-3">
        {doc.titulo && (
          <p
            className="mb-2 text-[15px] font-semibold"
            style={{ color: doc.color || undefined }}
          >
            {doc.titulo}
          </p>
        )}
        {previa.map((b, i) => {
          const acento = doc.color || '#1644EB';
          if (b.tipo === 'separador') {
            return <hr key={i} className="my-2.5 border-[#02062B]/[0.08]" />;
          }
          if (b.tipo === 'portada') {
            return (
              <div key={i} className="-mx-4 -mt-3 mb-3 px-4 py-4 text-white" style={{ backgroundColor: b.color || acento }}>
                <p className="text-[16px] font-semibold leading-tight">{b.texto}</p>
                {b.subtexto && <p className="mt-1 text-[12px] opacity-85">{b.subtexto}</p>}
                {b.detalle && <p className="mt-2 text-[10.5px] opacity-70">{b.detalle}</p>}
              </div>
            );
          }
          if (b.tipo === 'banda') {
            return (
              <p key={i} className="my-2 rounded-md px-2.5 py-1 text-[12.5px] font-semibold text-white" style={{ backgroundColor: b.color || acento }}>
                {b.texto}
              </p>
            );
          }
          if (b.tipo === 'cifras') {
            return (
              <div key={i} className="my-2 grid gap-1.5" style={{ gridTemplateColumns: `repeat(${Math.min(4, b.items.length)}, minmax(0, 1fr))` }}>
                {b.items.map((c, n) => (
                  <div key={n} className="rounded-lg px-2 py-1.5" style={{ backgroundColor: `${acento}14` }}>
                    <p className="truncate text-[15px] font-semibold" style={{ color: acento }}>{c.valor}</p>
                    <p className="truncate text-[10.5px] text-[#02062B]/[0.55]">{c.etiqueta}</p>
                  </div>
                ))}
              </div>
            );
          }
          if (b.tipo === 'tarjetas') {
            const cols = b.columnas ?? (b.items.length <= 2 ? 2 : 3);
            return (
              <div key={i} className="my-2 grid gap-1.5" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
                {b.items.slice(0, 6).map((t, n) => (
                  <div key={n} className="overflow-hidden rounded-lg border" style={{ backgroundColor: `${acento}0f`, borderColor: `${acento}33` }}>
                    {t.ref && doc.imagenes[t.ref] && (
                      <img src={doc.imagenes[t.ref]} alt="" className="aspect-[4/3] w-full object-cover" />
                    )}
                    <div className="px-2 py-1.5">
                      {t.cifra && <p className="text-[13px] font-semibold" style={{ color: acento }}>{t.cifra}</p>}
                      {t.titulo && <p className="truncate text-[11.5px] font-semibold text-brand-black">{t.titulo}</p>}
                      {t.texto && <p className="line-clamp-2 text-[10.5px] text-[#02062B]/[0.6]">{t.texto}</p>}
                    </div>
                  </div>
                ))}
              </div>
            );
          }
          if (b.tipo === 'ficha') {
            const url = b.ref ? doc.imagenes[b.ref] : undefined;
            return (
              <div key={i} className={`my-2 flex gap-3 ${b.lado === 'derecha' ? 'flex-row-reverse' : ''}`}>
                {url && <img src={url} alt="" className="h-[72px] w-[72px] shrink-0 rounded-lg object-cover" />}
                <div className="min-w-0 flex-1">
                  {b.titulo && <p className="text-[13px] font-semibold" style={{ color: acento }}>{b.titulo}</p>}
                  {b.texto && <p className="line-clamp-3 text-[12px] text-brand-black/85">{b.texto}</p>}
                  {b.detalle && (
                    <span className="mt-1 inline-block rounded-full px-2 py-0.5 text-[10.5px] font-semibold text-white" style={{ backgroundColor: acento }}>
                      {b.detalle}
                    </span>
                  )}
                </div>
              </div>
            );
          }
          if (b.tipo === 'columnas') {
            return (
              <div key={i} className="my-2 grid grid-cols-2 gap-3 text-[12px] text-brand-black/85">
                {[b.izquierda, b.derecha].map((t, n) => (
                  <div key={n}>
                    {b.titulos?.[n] && <p className="mb-0.5 font-semibold" style={{ color: acento }}>{b.titulos[n]}</p>}
                    <p className="line-clamp-4">{t}</p>
                  </div>
                ))}
              </div>
            );
          }
          if (b.tipo === 'etiquetas') {
            const color = b.color || acento;
            return (
              <div key={i} className="my-2 flex flex-wrap gap-1">
                {b.items.map((t, n) => (
                  <span key={n} className="rounded-full px-2 py-0.5 text-[10.5px] font-semibold" style={{ backgroundColor: `${color}1f`, color }}>
                    {textoDeItem(t)}
                  </span>
                ))}
              </div>
            );
          }
          if (b.tipo === 'nota') {
            const alineada = b.align === 'centro' ? 'text-center' : b.align === 'derecha' ? 'text-right' : '';
            return (
              <p key={i} className={`my-1 text-[11px] italic text-[#02062B]/[0.5] ${alineada}`}>
                {b.texto}
              </p>
            );
          }
          if (b.tipo === 'tabla') {
            return (
              <div key={i} className="my-2 overflow-x-auto">
                <table className="w-full border-collapse text-[12px]">
                  <tbody>
                    {b.filas.slice(0, 6).map((fila, f) => (
                      <tr
                        key={f}
                        className={`${f === 0 ? 'bg-[#02062B]/[0.03] font-medium' : ''} border-b border-[#02062B]/[0.05]`}
                      >
                        {fila.map((celda, c) => (
                          <td key={c} className="max-w-[160px] truncate px-2 py-1 align-top">
                            {celda}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
                {b.filas.length > 6 && (
                  <p className="pt-1 text-[11.5px] text-[#02062B]/[0.45]">
                    +{b.filas.length - 6} fila(s)
                  </p>
                )}
              </div>
            );
          }
          if (b.tipo === 'imagen') {
            const url = doc.imagenes[b.ref];
            return url ? (
              <img
                key={i}
                src={url}
                alt=""
                className="my-2 max-h-[140px] w-full rounded-lg object-contain"
              />
            ) : null;
          }
          if (b.tipo === 'destacado') {
            const color = b.color || doc.color || '#1644EB';
            return (
              <p
                key={i}
                className="my-2 rounded-lg py-2 px-3 text-[13px] text-brand-black/85"
                style={{ backgroundColor: `${color}1a`, borderLeft: `3px solid ${color}` }}
              >
                {b.texto}
              </p>
            );
          }
          if (b.tipo === 'lista') {
            return (
              <ul key={i} className="my-1.5 list-disc pl-5 text-[13px] text-brand-black/85">
                {b.items.map((it, n) => (
                  // La misma lectura tolerante que el PDF real: con el nivel "Baja" los
                  // ítems pueden llegar como objetos (ADR-2026-09-14-gemini-rechazaba-las-
                  // tools-del-chat). La vista previa tiene que mostrar lo mismo que se baja.
                  <li key={n}>{textoDeItem(it)}</li>
                ))}
              </ul>
            );
          }
          const alineado =
            b.align === 'centro' ? 'text-center' : b.align === 'derecha' ? 'text-right' : '';
          if (b.tipo === 'titulo') {
            return (
              <p
                key={i}
                className={`mb-1.5 mt-2 text-[15px] font-semibold ${alineado}`}
                style={{ color: b.color || doc.color || undefined }}
              >
                {b.texto}
              </p>
            );
          }
          if (b.tipo === 'subtitulo') {
            return (
              <p
                key={i}
                className={`mb-1 mt-2.5 text-[13.5px] font-semibold ${alineado}`}
                style={{ color: b.color || doc.color || undefined }}
              >
                {b.texto}
              </p>
            );
          }
          return (
            <p
              key={i}
              className={`my-1.5 text-[13px] leading-relaxed text-brand-black/85 ${alineado}`}
              style={{ color: b.color || undefined }}
            >
              {b.texto}
            </p>
          );
        })}
        {ocultos > 0 && (
          <p className="pt-2 text-[11.5px] text-[#02062B]/[0.45]">
            +{ocultos} bloque(s) más en el archivo.
          </p>
        )}
      </div>
      {error && (
        <p role="status" className="px-1 pt-2 text-[12px] text-[#C33B36]">
          {error}
        </p>
      )}
    </div>
  );
};

export default CrearPdfCard;
